/** The network's venue executors: the one at an address, and all of them in words. */

import { shortAddr } from "./address";
import type { VenueExecutor } from "./api";

/** The venue executor at `address`, when it is one. */
export function venueExecutor(executors: readonly VenueExecutor[] | null, address: string | null | undefined): VenueExecutor | null {
  if (executors === null || address === null || address === undefined) return null;
  return executors.find((e) => e.address.toLowerCase() === address.toLowerCase()) ?? null;
}

const andList = (items: string[]) => (items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]!}`);

/**
 * Every venue executor of the network in words, so an empty list is not
 * taken for a failed read: "the desk’s 0x2105…6692, the solver’s
 * 0xbbDC…b5F4, the earlier release’s 0x967a…7053, or the retired 0x00D0…9bce".
 */
export function venueWords(executors: readonly VenueExecutor[]): string {
  const desk = executors.filter((e) => e.current && e.role === "market-maker").map((e) => `the desk’s ${shortAddr(e.address)}`);
  const solver = executors.filter((e) => e.current && e.role === "solver").map((e) => `the solver’s ${shortAddr(e.address)}`);
  const earlier = executors.filter((e) => !e.current && e.fillable).map((e) => shortAddr(e.address));
  const retired = executors.filter((e) => !e.fillable).map((e) => shortAddr(e.address));
  const parts = [...desk, ...solver, ...(earlier.length === 0 ? [] : [`the earlier release’s ${andList(earlier)}`])];
  const last = retired.length === 0 ? null : `the retired ${andList(retired)}`;
  if (last === null) return parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]!}`;
  return parts.length === 0 ? last : `${parts.join(", ")}, or ${last}`;
}
